import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, CalendarClock, LayoutGrid, Plus, UserCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill, SectionPill } from "@/components/StatusPill";
import { RepairSearchAutocomplete } from "@/components/RepairSearchAutocomplete";

// Iter 180 — Columnas del Kanban de "Reparaciones" (orden exacto solicitado).
// "Espera de material" NO es un estado: es la SECCIÓN del estado "en_reparacion".
const ESPERA_MATERIAL = "espera de material";
const inEsperaMaterial = (o) => (o.section || "").trim().toLowerCase() === ESPERA_MATERIAL;
const STATUS_COLUMNS = [
  { key: "recibido", match: (o) => o.status === "recibido" },
  { key: "en_diagnostico", match: (o) => o.status === "en_diagnostico" },
  { key: "en_reparacion", match: (o) => o.status === "en_reparacion" && !inEsperaMaterial(o) },
  { key: "espera_material", espera: true, match: (o) => o.status === "en_reparacion" && inEsperaMaterial(o) },
  { key: "listo_para_entregar", match: (o) => o.status === "listo_para_entregar" },
];
const TAB_KEY = "my-repairs-tab";

const pad = (n) => String(n).padStart(2, "0");
const localISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
// Día natural (local) de la Fecha estimada; "" si no tiene.
export function repairDay(s) {
  if (!s) return "";
  if (s.length <= 10) return s.slice(0, 10);
  const d = new Date(s);
  return isNaN(d) ? s.slice(0, 10) : localISO(d);
}
const toUTC = (iso) => { const [y, m, d] = iso.split("-").map(Number); return Date.UTC(y, m - 1, d); };
const daysLeft = (day, today) => Math.round((toUTC(day) - toUTC(today)) / 86400000);

/** Iter 177 — "Mis reparaciones": Kanban por estado + Kanban de cuenta regresiva por técnico. */
export default function MyRepairs() {
  const { user, hasRole } = useAuth();
  const [tab, setTab] = useState(() => sessionStorage.getItem(TAB_KEY) || "kanban");
  const [techs, setTechs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const isTech = hasRole("technician");

  useEffect(() => {
    api.get("/repairs", { params: isTech ? { mine: true } : {} }).then((r) => setOrders(r.data || []))
      .catch(() => toast.error("No se pudieron cargar las reparaciones")).finally(() => setLoading(false));
    api.get("/repairs/tech-countdown/technicians", { params: { today: localISO(new Date()) } }).then((r) => setTechs(r.data || [])).catch(() => {});
  }, [isTech]);

  const tabs = useMemo(() => {
    const list = [...techs];
    if (!hasRole("admin") && isTech && user && !list.some((t) => t.id === user.id)) list.push({ id: user.id, name: user.name });
    return list;
  }, [techs, user, hasRole, isTech]);
  const activeTech = tabs.find((t) => `tech-${t.id}` === tab);
  const current = tab === "kanban" || !activeTech ? "kanban" : tab;
  const select = (k) => { setTab(k); sessionStorage.setItem(TAB_KEY, k); };

  return (
    <div className="p-6 lg:p-10" data-testid="my-repairs-page">
      <header className="flex items-end justify-between pb-6 gap-4 flex-wrap border-b border-dashed border-[#D5D7CE]">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[.14em] text-[#3F5222] font-semibold">
            <span className="text-[#8A9098]">//</span>&nbsp;&nbsp;Mi cola
          </div>
          <h1 className="font-display text-4xl lg:text-[38px] font-black mt-2 tracking-tight">Mis reparaciones</h1>
          <p className="text-sm text-[#4B5158] mt-1">{isTech ? "Órdenes asignadas a ti." : "Órdenes activas del taller."}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <RepairSearchAutocomplete testid="my-repairs-search" />
          <Link to="/repairs/new" data-testid="repair-new" className="ds-btn primary"><Plus size={15} /> Nueva orden</Link>
        </div>
      </header>
      <div className="mt-6 max-w-full overflow-x-auto">
        <div className="inline-flex border border-[#D5D7CE] bg-white p-1 gap-1 whitespace-nowrap" role="tablist" data-testid="my-repairs-tabs">
          <TabBtn active={current === "kanban"} onClick={() => select("kanban")} testid="my-repairs-tab-kanban" icon={LayoutGrid}>Reparaciones</TabBtn>
          {tabs.map((t) => (
            <TabBtn key={t.id} active={current === `tech-${t.id}`} onClick={() => select(`tech-${t.id}`)} testid={`my-repairs-tab-tech-${t.id}`} icon={UserCheck}>{t.name}</TabBtn>
          ))}
        </div>
      </div>

      <div className="mt-6">
        {current === "kanban"
          ? (loading ? <div className="text-sm text-[#525252]">Cargando…</div> : <StatusKanban orders={orders} />)
          : <CountdownKanban key={activeTech.id} tech={activeTech} />}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, testid, icon: Icon, children }) {
  return (
    <button type="button" role="tab" aria-selected={active} onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors ${active ? "bg-[#111110] text-white" : "text-[#4B5158] hover:bg-[#F7F7F4]"}`}>
      <Icon size={14} className="shrink-0" /> <span>{children}</span>
    </button>
  );
}

function fmtDate(s) {
  const d = repairDay(s).split("-");
  return d.length === 3 ? `${d[2]}/${d[1]}/${d[0]}` : "";
}

function RepairCard({ o, overdueDays }) {
  const navigate = useNavigate();
  const device = [o.device_type, o.brand, o.model].filter(Boolean).join(" · ");
  return (
    <button type="button" onClick={() => navigate(`/repairs/${o.id}`)} data-testid={`my-repair-card-${o.code}`}
      className={`ds-card w-full text-left p-3 bg-white hover:border-[#6E8B3D] hover:shadow-md transition-all group ${overdueDays ? "border-l-[3px] border-l-[#B42318]" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[11px] font-bold text-[#3F5222]">{o.code}</span>
        {o.scheduled_at && (
          <span className="inline-flex items-center gap-1 font-mono text-[10px] text-[#525252]" data-testid={`my-repair-card-${o.code}-date`} title="Fecha estimada">
            <CalendarClock size={11} /> {fmtDate(o.scheduled_at)}
          </span>
        )}
      </div>
      <div className="font-semibold text-sm mt-1 truncate group-hover:text-[#3F5222]">{device || "—"}</div>
      <div className="text-xs text-[#525252] truncate">{o.customer_name || "—"}</div>
      {o.serial && <div className="font-mono text-[10px] text-[#8A9098] truncate mt-0.5">S/N {o.serial}</div>}
      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
        <StatusPill status={o.status} />
        {o.section && <SectionPill section={o.section} status={o.status} />}
        {overdueDays > 0 && <span className="font-mono text-[10px] font-bold text-[#B42318]" data-testid={`my-repair-card-${o.code}-overdue`}>Vencida hace {overdueDays} d</span>}
      </div>
    </button>
  );
}

function Column({ testid, countTestid, header, items, danger, renderCard }) {
  return (
    <section className={`ds-card p-3 ${danger ? "bg-[#FDF3F2] border-[#F1C9C4]" : "bg-[#FAFAF8]"}`} data-testid={testid}>
      <header className="flex items-center justify-between pb-2 border-b border-[#E5E5E0] gap-2">
        {header}
        <span className="font-mono text-[11px] px-2 py-0.5 rounded-full bg-white border border-[#E5E5E0] text-[#525252]" data-testid={countTestid}>{items.length}</span>
      </header>
      <div className="mt-3 space-y-2">
        {items.length === 0 ? <div className="text-xs text-[#8A9098] text-center py-6">Sin reparaciones</div> : items.map(renderCard)}
      </div>
    </section>
  );
}

const Board = ({ testid, children }) => (
  <div className="overflow-x-auto pb-2 -mx-6 px-6 lg:mx-0 lg:px-0">
    <div className="grid grid-flow-col auto-cols-[minmax(240px,1fr)] gap-4" data-testid={testid}>{children}</div>
  </div>
);

/* Pestaña 1: Kanban por estado. Orden: Recibido | En diagnóstico | En reparación | Espera de material | Listo para entregar. */
function StatusKanban({ orders }) {
  return (
    <Board testid="my-repairs-kanban">
      {STATUS_COLUMNS.map((c) => (
        <Column key={c.key} testid={`my-kanban-col-${c.key}`} countTestid={`my-kanban-count-${c.key}`}
          header={c.espera ? <SectionPill section="Espera de material" status="en_reparacion" /> : <StatusPill status={c.key} />}
          items={orders.filter(c.match)} renderCard={(o) => <RepairCard key={o.id} o={o} />} />
      ))}
    </Board>
  );
}

function useToday() {
  const [today, setToday] = useState(() => localISO(new Date()));
  useEffect(() => {
    const t = setInterval(() => setToday(localISO(new Date())), 60000);
    return () => clearInterval(t);
  }, []);
  return today;
}

const byDate = (a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || "") || (a.code || "").localeCompare(b.code || "");

/* Pestañas de técnico: cuenta regresiva por Fecha estimada (días naturales, calculada en cliente). */
function CountdownKanban({ tech }) {
  const today = useToday();
  const [orders, setOrders] = useState(null);
  const load = useCallback(() => {
    api.get("/repairs/tech-countdown", { params: { technician_id: tech.id, today } }).then((r) => setOrders(r.data || []))
      .catch(() => { setOrders([]); toast.error("No se pudo cargar el Kanban del técnico"); });
  }, [tech.id, today]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, [load]);

  const { overdue, dayCols, nodate } = useMemo(() => {
    const overdue = [], nodate = [], days = Array.from({ length: 8 }, () => []);
    (orders || []).forEach((o) => {
      const day = repairDay(o.scheduled_at);
      if (!day) return nodate.push(o);
      const n = daysLeft(day, today);
      if (n < 0) overdue.push({ ...o, _overdue: -n });
      else if (n <= 7) days[n].push(o);
    });
    overdue.sort((a, b) => b._overdue - a._overdue || byDate(a, b)); // más vencida → menos vencida
    const dayCols = days.map((items, n) => ({
      key: `d${n}`, label: n === 0 ? "Estimados para hoy" : `${n} día${n > 1 ? "s" : ""}`, items: items.sort(byDate),
    }));
    nodate.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    return { overdue, dayCols, nodate };
  }, [orders, today]);

  const total = overdue.length + dayCols.reduce((s, c) => s + c.items.length, 0) + nodate.length;
  return (
    <div data-testid="my-tech-kanban-wrap">
      <h2 className="font-display text-lg font-bold" data-testid="my-tech-kanban-title">Reparaciones asignadas a {tech.name}</h2>
      <div className="text-xs text-[#525252] font-mono uppercase tracking-widest mt-1 mb-3" data-testid="my-tech-kanban-summary">
        {orders ? `${total} reparaciones · cuenta regresiva por Fecha estimada (próximos 7 días)` : "Cargando…"}
      </div>
      {orders && (
        <>
          {/* Fila superior a ancho completo: Vencidas */}
          <section className="ds-card p-3 bg-[#FDF3F2] border-[#F1C9C4] mb-4" data-testid="my-tech-col-overdue">
            <header className="flex items-center justify-between pb-2 border-b border-[#F1C9C4] gap-2">
              <span className="inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-wider text-[#B42318]">
                <AlertTriangle size={12} /> Vencidas
              </span>
              <span className="font-mono text-[11px] px-2 py-0.5 rounded-full bg-white border border-[#F1C9C4] text-[#B42318]" data-testid="my-tech-count-overdue">{overdue.length}</span>
            </header>
            {overdue.length === 0 ? (
              <div className="text-xs text-[#8A9098] text-center py-4">Sin reparaciones vencidas</div>
            ) : (
              <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
                {overdue.map((o) => (
                  <div key={o.id} className="w-[240px] shrink-0"><RepairCard o={o} overdueDays={o._overdue} /></div>
                ))}
              </div>
            )}
          </section>
          {/* Kanban de cuenta regresiva: Estimados para hoy → 7 días (+ Sin fecha si aplica) */}
          <Board testid="my-tech-kanban">
            {dayCols.map((c) => (
              <Column key={c.key} testid={`my-tech-col-${c.key}`} countTestid={`my-tech-count-${c.key}`} items={c.items}
                header={<span className={`inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-wider ${c.key === "d0" ? "text-[#3F5222]" : "text-[#4B5158]"}`}>{c.label}</span>}
                renderCard={(o) => <RepairCard key={o.id} o={o} />} />
            ))}
            {nodate.length > 0 && (
              <Column key="nodate" testid="my-tech-col-nodate" countTestid="my-tech-count-nodate" items={nodate}
                header={<span className="inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-wider text-[#4B5158]">Sin fecha</span>}
                renderCard={(o) => <RepairCard key={o.id} o={o} />} />
            )}
          </Board>
        </>
      )}
    </div>
  );
}
